// AutomationRuleConditionsSection.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import AutomationRuleConditions from './AutomationRuleConditions';
import Button from 'components/button/Button';
import ModalField from 'components/modal/field/ModalField';
import ModalLabel from 'components/modal/label/ModalLabel';

/**
 * AutomationRuleConditionsSection
 *
 * Renders the conditions header, rows, and add-condition action for an automation rule.
 *
 * @param {object} rule - The automation rule being edited.
 * @param {array} fieldOptions - Available fields for trigger and condition selection.
 * @param {object} fieldTypeByKey - Field type lookup keyed by field key.
 * @param {object} fieldEnumOptionsByKey - Enum options lookup keyed by field key.
 * @param {function} updateConditionRows - Updates condition rows for a rule.
 * @param {function} handleRemoveCondition - Removes a condition row from a rule.
 * @param {function} handleAddCondition - Adds a condition row to a rule.
 *
 * @returns {ReactNode} The rendered automation rule conditions section.
 */
const AutomationRuleConditionsSection = ({
    rule,
    fieldOptions,
    fieldTypeByKey,
    fieldEnumOptionsByKey,
    updateConditionRows,
    handleRemoveCondition,
    handleAddCondition,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // ----- RENDER ----- //

    return (
        <>
            <ModalField>
                <ModalLabel label="If..." />

                <AutomationRuleConditions
                  rule={rule}
                  theme={theme}
                  fieldOptions={fieldOptions}
                  fieldTypeByKey={fieldTypeByKey}
                  fieldEnumOptionsByKey={fieldEnumOptionsByKey}
                  updateConditionRows={updateConditionRows}
                  handleRemoveCondition={handleRemoveCondition}
                />
            </ModalField>

            {/* ADD BUTTON */}
            <div className="automation-rule-add-button">
                <Button
                  label="+ Add Condition"
                  fontSize="12px"
                  fontWeight="400"
                  foregroundColor={theme.foregroundMuted}
                  backgroundColor="transparent"
                  height="30px"
                  padding="0 10px"
                  onClick={() => handleAddCondition(rule.key)}
                />
            </div>
        </>
    );
};

// Dev Tools
AutomationRuleConditionsSection.displayName = 'AutomationRuleConditionsSection';

export default AutomationRuleConditionsSection;
